import { action, computed, makeObservable, observable } from 'mobx'

import { ISharedSitePermit } from '~/client/graph'
import { TwoMonthsDatePickerMode } from '~/client/src/desktop/components/TwoMonthsDatePicker/TwoMonthsDatePicker.store'
import CalendarPlaceholderStore from '~/client/src/shared/components/CalendarDayView/components/CalendarPlaceholder.store'
import IEditableCalendarEvent from '~/client/src/shared/components/CalendarDayView/components/IEditableCalendarEvent'
import CalendarEventLabelType from '~/client/src/shared/enums/CalendarEventLabelType'
import DefaultApps from '~/client/src/shared/enums/DefaultApps'
import CalendarEvent, {
  CalendarEventEntityType,
} from '~/client/src/shared/models/CalendarEvent'
import GlobeView from '~/client/src/shared/models/GlobeView'
import Sitemap from '~/client/src/shared/models/Sitemap'
import Workflow from '~/client/src/shared/models/Workflow'
import BasemapsStore from '~/client/src/shared/stores/domain/Basemaps.store'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import SharedSitePermitStore from '~/client/src/shared/stores/domain/SharedSitePermit.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import TilesetsStore from '~/client/src/shared/stores/domain/Tilesets.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowsStore from '~/client/src/shared/stores/domain/Workflows.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import EventContext from '~/client/src/shared/stores/EventStore/EventContext'
import { IFilters } from '~/client/src/shared/stores/InitialState'
import BaseLogisticsStore from '~/client/src/shared/stores/ui/BaseLogistics.store'
import ProjectDateStore, {
  DAYS_IN_WEEK,
} from '~/client/src/shared/stores/ui/ProjectDate.store'
import WorkflowDrawingGuardStore from '~/client/src/shared/stores/ui/WorkflowDrawingGuard.store'
import { NOOP } from '~/client/src/shared/utils/noop'

import { IDateFilters } from '../../../../components/Filters/DateSelector/DateSelector.store'
import ViewModes from '../../../../enums/ViewModes'
import DesktopInitialState from '../../../../stores/DesktopInitialState'
import DesktopEventStore from '../../../../stores/EventStore/DesktopEvents.store'

export default class LogisticsStore extends BaseLogisticsStore {
  @observable public selectedSharedEntity: ISharedSitePermit = null
  @observable public isFormTypeSelectionActive: boolean = false
  @observable public isPermitCreationActive: boolean = false
  @observable public selectedSitemap: Sitemap = null
  @observable public selectedGlobe: GlobeView = null
  @observable public workflowToDuplicate: Workflow = null

  @observable public isPermitDialogDisplayed: boolean = false
  @observable public onCloseCallback: () => void = NOOP
  @observable public selectedWorkflow: Workflow = null
  @observable public selectedDatePickerMode: TwoMonthsDatePickerMode =
    TwoMonthsDatePickerMode.ONE_DAY
  @observable public selectedDatePickerModeIsDirty: boolean = false

  @observable public editableCalendarEvent: IEditableCalendarEvent = null
  @observable public isDateMoveConfirmModalOpen: boolean = false
  @observable private _allForms: Workflow[] = []

  public calendarDatePickerStates = [
    TwoMonthsDatePickerMode.ONE_DAY,
    TwoMonthsDatePickerMode.FIXED_WEEK,
    TwoMonthsDatePickerMode.MONTH,
  ]

  public listDatePickerStates = [
    TwoMonthsDatePickerMode.ONE_DAY_DEFAULT_TODAY,
    TwoMonthsDatePickerMode.FIXED_WEEK,
    TwoMonthsDatePickerMode.FIXED_THREE_WEEKS,
    TwoMonthsDatePickerMode.MONTH,
    TwoMonthsDatePickerMode.FIXED_SIX_WEEKS,
    TwoMonthsDatePickerMode.THREE_MONTHS,
    TwoMonthsDatePickerMode.YEAR,
    TwoMonthsDatePickerMode.PROJECT,
  ]

  public ganttDatePickerStates = [
    TwoMonthsDatePickerMode.ONE_DAY_DEFAULT_TODAY,
    TwoMonthsDatePickerMode.FIXED_WEEK,
    TwoMonthsDatePickerMode.FIXED_TWO_WEEKS,
    TwoMonthsDatePickerMode.FIXED_THREE_WEEKS,
    TwoMonthsDatePickerMode.FIXED_FOUR_WEEKS,
    TwoMonthsDatePickerMode.FIXED_SIX_WEEKS,
  ]

  @computed
  public get pickerModeInDays(): number {
    const { user } = this.state

    if (!user) {
      return DAYS_IN_WEEK
    }

    switch (this.selectedDatePickerMode) {
      case TwoMonthsDatePickerMode.ONE_DAY:
      case TwoMonthsDatePickerMode.ONE_DAY_DEFAULT_TODAY:
        return 1
      case TwoMonthsDatePickerMode.FIXED_WEEK:
        return DAYS_IN_WEEK
      case TwoMonthsDatePickerMode.FIXED_TWO_WEEKS:
        return DAYS_IN_WEEK * 2
      case TwoMonthsDatePickerMode.FIXED_THREE_WEEKS:
        return DAYS_IN_WEEK * 3
      case TwoMonthsDatePickerMode.FIXED_FOUR_WEEKS:
        return DAYS_IN_WEEK * 4
      case TwoMonthsDatePickerMode.FIXED_SIX_WEEKS:
        return DAYS_IN_WEEK * 6
    }
  }

  public dateFilters = observable<IDateFilters>({
    startDate: null,
    daysToAdd: 0,
    isDateFilterActive: true,
  })

  public clearPostEventCallback: () => void = NOOP

  @computed
  public get displayedWorkflow(): Workflow {
    if (!this.selectedWorkflow) return null

    return (
      this.allForms.find(f => f.id === this.selectedWorkflow.id)?.copy() ||
      this.selectedWorkflow.copy()
    )
  }

  public constructor(
    protected readonly workflowsStore: WorkflowsStore,
    protected readonly projectDateStore: ProjectDateStore,
    protected readonly eventsStore: DesktopEventStore,
    protected readonly sitemapsStore: SitemapsStore,
    protected readonly globeViewsStore: GlobeViewsStore,
    protected readonly tilesetsStore: TilesetsStore,
    closuresStore: ClosuresStore,
    protected readonly sharedSitePermitStore: SharedSitePermitStore,
    workflowTypesStore: WorkflowTypesStore,
    workflowCategoriesStore: WorkflowCategoriesStore,
    locationAttributesStore: LocationAttributesStore,
    private readonly basemapsStore: BasemapsStore,
    protected readonly calendarPlaceholderStore?: CalendarPlaceholderStore,
    companiesStore?: CompaniesStore,
    private readonly workflowDrawingGuardStore?: WorkflowDrawingGuardStore,
  ) {
    super(
      projectDateStore,
      eventsStore,
      sitemapsStore,
      globeViewsStore,
      workflowTypesStore,
      workflowCategoriesStore,
      locationAttributesStore,
      companiesStore,
      closuresStore,
      sharedSitePermitStore,
    )

    this.setInitialDateValue()
    makeObservable(this)
  }

  public get isOneDayCalendarModeActive() {
    return (
      this.selectedDatePickerMode === TwoMonthsDatePickerMode.ONE_DAY ||
      this.selectedDatePickerMode ===
        TwoMonthsDatePickerMode.ONE_DAY_DEFAULT_TODAY
    )
  }

  public get isProjectCalendarModeActive(): boolean {
    return this.selectedDatePickerMode === TwoMonthsDatePickerMode.PROJECT
  }

  public initPostEventCallback = (): void => {
    this.clearPostEventCallback = this.eventsStore.addPostEventCallback(
      this.onEvent,
    )
  }

  public get allForms(): Workflow[] {
    return this._allForms
  }

  @action.bound
  public setAllForms(forms: Workflow[]) {
    this._allForms = forms
  }

  public setSelectedSharedEntity(sharedEntity: ISharedSitePermit): void {
    this.hideAllDialogs()
    this.hideFormTypeSelectionForm()
    this.selectedSharedEntity = sharedEntity
  }

  public resetSelectedSharedEntity(): void {
    this.selectedSharedEntity = null
  }

  @action.bound
  public setEditableEvent(event: IEditableCalendarEvent) {
    this.editableCalendarEvent = event
  }

  @action.bound
  public openDateMoveConfirmModal() {
    this.isDateMoveConfirmModalOpen = true
  }

  @action.bound
  public hideDateMoveConfirmModal() {
    this.isDateMoveConfirmModalOpen = false
    this.calendarPlaceholderStore.resetEventPlaceholder()
  }

  @action.bound
  public onEventClicked(event: CalendarEvent) {
    const isNewType = event.labelType === CalendarEventLabelType.New

    if (isNewType || event.isShared) {
      this.setSelectedSharedEntity(event.data)
      return
    }

    switch (event.entityType) {
      case CalendarEventEntityType.Form:
        this.showWorkflowViewDialog(event.data)
        break
    }

    if (this.calendarPlaceholderStore.eventPlaceholder) {
      this.calendarPlaceholderStore.resetEventPlaceholder()
    }
  }

  @action.bound
  public onEvent(eventContext: EventContext): void {
    const [eventType] = eventContext.event

    switch (eventType) {
      case e.ACTIVATE_PROJECT:
        return this.hideAllDialogs()
      case e.SITEMAPS_RECEIVED:
      case e.SITEMAP_UPDATED:
      case e.GLOBE_VIEWS_RECEIVED:
      case e.GLOBE_VIEW_UPDATED:
      case e.GLOBE_VIEW_ITEM_DATA_UPDATED:
      case e.SITEMAP_ITEM_DATA_UPDATED:
      case e.LOGISTICS_CONFIGURATIONS_RECEIVED:
      case e.LOGISTICS_CONFIGURATIONS_UPDATED:
      case e.WORKFLOWS_CONFIGURATIONS_RECEIVED:
      case e.WORKFLOWS_CONFIGURATIONS_UPDATED:
        this.ensureCorrectViewMode()
    }
  }

  @action.bound
  public setInitialDateMode(): void {
    switch (this.viewMode) {
      case ViewModes.Calendar:
        if (this.shouldSkipInitialDateModeChange(this.calendarDatePickerStates))
          return

        this.changeDatePickerMode(TwoMonthsDatePickerMode.FIXED_WEEK)
        this.setInitialDateValue()

        break
      case ViewModes.List:
        if (this.shouldSkipInitialDateModeChange(this.listDatePickerStates))
          return

        this.changeDatePickerMode(TwoMonthsDatePickerMode.ONE_DAY_DEFAULT_TODAY)
        break
      case ViewModes.Map:
        if (this.shouldSkipInitialDateModeChange(this.listDatePickerStates))
          return

        this.changeDatePickerMode(TwoMonthsDatePickerMode.FIXED_WEEK)
        break
      case ViewModes.Gantt:
        if (this.shouldSkipInitialDateModeChange(this.ganttDatePickerStates))
          return

        this.changeDatePickerMode(TwoMonthsDatePickerMode.FIXED_WEEK)
        break
    }
  }

  private shouldSkipInitialDateModeChange(
    allowedModes: TwoMonthsDatePickerMode[],
  ): boolean {
    return (
      allowedModes.includes(this.selectedDatePickerMode) &&
      this.selectedDatePickerModeIsDirty
    )
  }

  @action.bound
  public setInitialDateValue(): void {
    if (this.viewMode === ViewModes.List) {
      this.dateFilters.startDate = this.projectDateStore.startOfDay(new Date())
      return
    }

    this.dateFilters.startDate = this.projectDateStore.startOfWeek(new Date())
  }

  @action.bound
  public changeDatePickerMode(mode: TwoMonthsDatePickerMode): void {
    this.selectedDatePickerMode = mode
    this.selectedDatePickerModeIsDirty = true
  }

  public get state(): DesktopInitialState {
    return this.eventsStore.appState
  }

  public get filters(): IFilters {
    return this.state.workflowsFilters
  }

  public get viewMode(): ViewModes {
    return this.state.formsList.viewMode
  }

  public get isCalendarViewMode(): boolean {
    return this.viewMode === ViewModes.Calendar
  }

  @action
  private setViewMode(viewMode: ViewModes) {
    this.state.formsList.viewMode = viewMode
    localStorage.setItem(DefaultApps.formsViewKey, viewMode)
  }

  @action.bound
  public ensureCorrectViewMode(): void {
    if (
      this.viewMode === ViewModes.Map &&
      this.isMapViewDisabled &&
      !this.isLoaderShown
    ) {
      this.setViewMode(ViewModes.List)
    }
  }

  @action.bound
  public openFormTypeSelectionForm(): void {
    // "+ Create new" starts the alternative creation path — confirm first when
    // a map-first drawing session would lose markups (no-op otherwise).
    const open = action(() => {
      this.hidePermitDialog()
      this.hidePermitCreationForm()
      this.resetSelectedSharedEntity()
      this.isFormTypeSelectionActive = true
    })
    if (this.workflowDrawingGuardStore) {
      this.workflowDrawingGuardStore.guard(open)
    } else {
      open()
    }
  }

  @action.bound
  public hideFormTypeSelectionForm(): void {
    this.isFormTypeSelectionActive = false
    this.editableCalendarEvent = null
    this.calendarPlaceholderStore.resetEventPlaceholder()
  }

  public onFormTypeSelectionFormClose = (): void => {
    this.hideFormTypeSelectionForm()
    this.resetPreSelectedSiteLocation()
  }

  @action.bound
  public resetPreSelectedSiteLocation(): void {
    this.state.preSelectedSiteLocation = null
  }

  // Map-first creation whose type the composer cannot finish: same handoff the type
  // picker performs, minus the picking. Not guarded — this route carries the drawn
  // markups over rather than discarding them.
  @action.bound
  public openWorkflowCreationForm(permitTypeId: string): void {
    this.state.preSelectedPermitTypeId = permitTypeId
    this.openPermitCreationForm()
  }

  @action.bound
  public openPermitCreationForm(): void {
    this.hidePermitDialog()
    this.hideFormTypeSelectionForm()
    this.isPermitCreationActive = true
  }

  public onPermitCreationFormClose = (): void => {
    this.hidePermitCreationForm()
    this.resetPreSelectedSiteLocation()
  }

  @action.bound
  public hidePermitCreationForm(): void {
    this.isPermitCreationActive = false
    this.workflowToDuplicate = null
    this.editableCalendarEvent = null
    this.calendarPlaceholderStore.resetEventPlaceholder()
  }

  @action.bound
  public startDuplicateWorkflow(permit: Workflow): void {
    this.hideAllDialogs()
    this.workflowToDuplicate = permit?.copy?.() || permit
    this.isPermitCreationActive = true
  }

  @action.bound
  public showWorkflowViewDialog(permit: Workflow, onClose?: () => void): void {
    const sharedPermit = this.sharedSitePermitStore.getById(permit.id)
    if (sharedPermit) {
      this.setSelectedSharedEntity(sharedPermit)
      return
    }

    this.hideAllDialogs()
    this.selectedWorkflow = permit
    this.isPermitDialogDisplayed = true
    this.onCloseCallback = onClose
  }

  @action.bound
  public hidePermitDialog(): void {
    this.onCloseCallback?.()
    this.selectedWorkflow = null
    this.isPermitDialogDisplayed = false
    this.isDateMoveConfirmModalOpen = false
    this.editableCalendarEvent = null
  }

  @action.bound
  public hideAllDialogs(): void {
    this.resetSelectedSharedEntity()
    this.hidePermitCreationForm()
    this.hidePermitDialog()
    this.closeLinkedWorkflowPreview()
  }

  public get isLoaderShown(): boolean {
    const { workflows, loading } = this.state
    const isBaseLoaderShown =
      !this.sitemapsStore.isDataReceived ||
      !this.closuresStore.isDataReceived ||
      !this.companiesStore.isDataReceived ||
      !this.globeViewsStore.isDataReceived ||
      !this.basemapsStore.isDataReceived ||
      !(this.state.isTilesetsDisabled || this.tilesetsStore.isDataReceived)
    const areFormsLoading =
      !this.permitTypesStore.isDataReceived ||
      !this.workflowsStore.isDataReceived ||
      !this.formCategoriesStore.isDataReceived

    return (
      isBaseLoaderShown ||
      areFormsLoading ||
      !workflows.isDataReceived ||
      loading.get(e.GET_WORKFLOW_CUSTOM_FILTERS)
    )
  }

  public get isDateFilterActive(): boolean {
    const { isDateFilterActive } = this.dateFilters
    return isDateFilterActive
  }

  public get startDate(): Date {
    const { startDate } = this.dateFilters
    return this.isDateFilterActive
      ? startDate
      : this.projectDateStore.getMinAvailableDate()
  }

  public get endDate(): Date {
    if (!this.isDateFilterActive) {
      return this.projectDateStore.getMaxAvailableDate()
    }

    return this.projectDateStore.getEndDate(this.dateFilters)
  }
}
